import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";
import { useConnectors } from "./connectors";
import type { TokenSummary } from "./tokens";

/**
 * API Tools — request builder proti eTTS loopback + per-connector execute
 * endpointům (`/admin/api/v1/tools/api/{connector}/execute/{operation}`).
 * Odpověď je envelope `{success, httpStatus, request, result, error}`
 * + volitelná `X-API-Stats` hlavička. Metadatově-řízený runner (viz catalog).
 */

/** eTTS loopback sentinel — server ho nahradí admin JWT z HttpOnly cookie. */
export const ADMIN_JWT_SENTINEL = "__USE_ADMIN_JWT_COOKIE__";

export interface ApiToolsConnector {
  id: string; // ecobra | echannels | kb-caas | privat | etts
  label: string;
  type: string | null; // AuthServerType (null pro etts loopback)
}

const CONNECTOR_MAP: Record<string, { id: string; label: string }> = {
  ECOBRA: { id: "ecobra", label: "eCobra" },
  CSOB_ECHANNELS: { id: "echannels", label: "eChannels" },
  KB_CAAS: { id: "kb-caas", label: "KB CAAS" },
  PRIVAT: { id: "privat", label: "Privat" },
};

const TAB_ORDER = ["ecobra", "echannels", "kb-caas", "privat", "etts"];

/**
 * `AuthServerType` → id konektorového tabu. Na eTTS loopback záložce se cílový
 * konektor volí polem `authServerType`, takže podle něj musí jít nabídnout i
 * konektor-specifická pole (jinak by šla vyplnit jen přes obecný key/value editor).
 * Vrací `null` pro typ, který v tomto buildu není (nebo pro nevybráno).
 */
export function connectorIdForAuthServerType(authServerType: string): string | null {
  return CONNECTOR_MAP[authServerType]?.id ?? null;
}

/** Connector taby odvozené z povolených konektorů + vždy eTTS loopback. */
export function useApiToolsConnectors(): { data: ApiToolsConnector[]; isLoading: boolean } {
  const { data, isLoading } = useConnectors();
  const tabs: ApiToolsConnector[] = (data ?? [])
    .filter((c) => c.enabled && CONNECTOR_MAP[c.connectorType])
    .map((c) => ({ ...CONNECTOR_MAP[c.connectorType], type: c.connectorType }));
  tabs.push({ id: "etts", label: "eTTS", type: null });
  tabs.sort((a, b) => TAB_ORDER.indexOf(a.id) - TAB_ORDER.indexOf(b.id));
  return { data: tabs, isLoading };
}

/** Všechny tokeny pro pickery (summary; heslo se dotahuje z detailu). */
export function useAllTokens() {
  return useQuery({
    queryKey: ["tokens"],
    queryFn: () => apiFetch<TokenSummary[]>("/api/v1/tokens"),
  });
}

/** Heslo tokenu (z detailu) — pro valid/invalid PIN helper. */
export async function fetchTokenPassword(tokenId: string): Promise<string> {
  const detail = await apiFetch<{ password?: string | null }>(`/api/v1/tokens/${encodeURIComponent(tokenId)}`);
  return detail.password ?? "";
}

/** Náhodné (chybné) heslo stejné délky — invalid-PIN simulace. */
export function invalidPassword(real: string): string {
  const len = real.length || 6;
  let pw = "";
  do {
    pw = "";
    for (let i = 0; i < len; i++) pw += Math.floor(Math.random() * 10).toString();
  } while (pw === real);
  return pw;
}

export interface ExecuteEnvelope {
  success: boolean;
  httpStatus?: number;
  request?: {
    method?: string;
    url?: string;
    headers?: Record<string, string>;
    body?: unknown;
  };
  result?: unknown;
  error?: string;
  message?: string;
}

/**
 * Jedna položka výpisu čekajících transakcí (`TransactionPendingResponse` na serveru).
 *
 * `encryptedMessage` je hodnota, kterou schválení očekává v poli `secureMessage` —
 * podepisuje se **zašifrovaná** zpráva, ne `transaction.decryptedMessage`. `status`,
 * `type` a `transaction` plní jen některé konektory / jen při `includeDetails`.
 */
export interface PendingTransactionItem {
  transactionId: string;
  status?: string;
  type?: string;
  receivedAt?: string;
  encryptedMessage?: string;
  expiresAt?: string;
  transaction?: {
    decryptedData?: Record<string, unknown>;
    decryptedMessage?: string;
    wysiwygData?: string;
  };
}

export interface ApiStats {
  totalDurationMs?: number;
  connectorDurationMs?: number;
  processingDurationMs?: number;
  apiCallCount?: number;
  connectorCalls?: unknown[];
}

export interface ExecuteResponse {
  envelope: ExecuteEnvelope;
  stats: ApiStats | null;
  /** Pre-controller security block (401/403 bez `success` — typicky stale CSRF/session). */
  securityBlock: boolean;
  rawStatus: number;
}

/**
 * Provede jednu API Tools operaci. Vlastní fetch (ne apiFetch) kvůli čtení
 * `X-API-Stats` hlavičky a rozpoznání pre-controller security bloku.
 */
export async function executeApiTool(
  connector: string,
  operation: string,
  body: Record<string, unknown>,
): Promise<ExecuteResponse> {
  const res = await fetch(`/admin/api/v1/tools/api/${connector}/execute/${operation}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ ...body, connector }),
  });

  let stats: ApiStats | null = null;
  const statsHeader = res.headers.get("X-API-Stats");
  if (statsHeader) {
    try {
      stats = JSON.parse(statsHeader) as ApiStats;
    } catch {
      stats = null;
    }
  }

  const text = await res.text();
  let envelope: ExecuteEnvelope;
  try {
    envelope = JSON.parse(text) as ExecuteEnvelope;
  } catch {
    // Nevalidní JSON (typicky Spring error stránka u 401/403).
    return {
      envelope: { success: false, httpStatus: res.status, error: text || `HTTP ${res.status}` },
      stats,
      securityBlock: res.status === 401 || res.status === 403,
      rawStatus: res.status,
    };
  }

  // Náš executor vždy nastaví `success`. Chybí-li AND je 401/403 → security block.
  const securityBlock = (res.status === 401 || res.status === 403) && envelope.success === undefined;
  return { envelope, stats, securityBlock, rawStatus: res.status };
}

// --- KB CAAS lokální dictionary cache ---

export interface KbDictionarySummary {
  version: number;
  fetchedAt: string;
  contentLength: number;
}

export interface KbDictionaryContent {
  version: number;
  fetchedAt: string;
  content: string;
}

const KB_DICT_KEY = ["kb-dictionaries"];

export function useKbDictionaries() {
  return useQuery({
    queryKey: KB_DICT_KEY,
    queryFn: () => apiFetch<KbDictionarySummary[]>("/admin/api/v1/tools/api/kb-caas/dictionaries"),
  });
}

export function fetchKbDictionaryContent(version: number): Promise<KbDictionaryContent> {
  return apiFetch<KbDictionaryContent>(`/admin/api/v1/tools/api/kb-caas/dictionaries/${version}`);
}

export function useDeleteKbDictionary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) =>
      apiFetch(`/admin/api/v1/tools/api/kb-caas/dictionaries/${version}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KB_DICT_KEY }),
  });
}

/** Invalidace dictionary cache (po úspěšném kbCaasGetDictionary). */
export function useInvalidateKbDictionaries() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: KB_DICT_KEY });
}

// ==================== Privat: Bearer token pro API Tools ====================

/**
 * Stav Bearer tokenu, který si API Tools drží pro volání na Privat Mobile API.
 *
 * **Platnost je odhad, ne údaj od serveru.** Privat Mobile API dobu platnosti nevrací
 * (ani refresh token), takže se drží pevný interval; token může na jejich straně přestat
 * platit dřív. Proto `expiryEstimated` — rozhraní to má říct nahlas, ne tvářit se jistě.
 */
export interface PrivatTokenStatus {
  /** Zda si API Tools token obstarává samo. */
  autoLogin: boolean;
  hasToken: boolean;
  accessToken?: string;
  source?: string;
  obtainedAt?: string;
  expiresAt?: string;
  /** Zbývající sekundy odhadu; bez tokenu celková délka odhadu. */
  ttlSeconds: number;
  expiryEstimated: boolean;
}

/** Jeden krok přihlášení tak, jak ho vrací operace `login`. */
export interface PrivatLoginStep {
  step: string;
  call: string;
  ok: boolean;
  status?: number | null;
  detail?: string | null;
}

export interface PrivatLoginResult {
  success: boolean;
  accessToken?: string;
  source?: string;
  cached?: boolean;
  obtainedAt?: string;
  error?: string;
  failedStep?: string;
  steps?: PrivatLoginStep[];
}

/**
 * Stav se čte i při každé změně vybraného tokenu, takže endpoint **nepřihlašuje** —
 * jinak by pouhé prohlížení stránky bušilo do auth serveru.
 */
export function usePrivatTokenStatus(tokenId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["privat-token-status", tokenId],
    queryFn: () =>
      apiFetch<PrivatTokenStatus>(
        `/admin/api/v1/tools/api/privat/token-status?tokenId=${encodeURIComponent(tokenId)}`,
      ),
    enabled,
    refetchOnWindowFocus: false,
  });
}

/** Ruční přihlášení / zahození tokenu; obojí jde přes obvyklý execute endpoint. */
export function usePrivatTokenAction(tokenId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: "login" | "force" | "invalidate") =>
      apiFetch<PrivatLoginResult>(
        `/admin/api/v1/tools/api/privat/execute/${action === "invalidate" ? "invalidateLogin" : "login"}`,
        { method: "POST", json: { tokenId, force: action === "force" } },
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["privat-token-status", tokenId] }),
  });
}
